import React, { useState, useCallback, useRef, memo } from 'react';
import { Upload, ImageIcon, X } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { FILE_SIZE_LIMITS, formatFileSize, MB } from '@/constants/fileSize';
import { fileToBase64 } from '@/utils/fileToBase64';

interface ImageUploaderProps {
  onImageSelect: (base64: string, file: File) => void;
  onRemove?: () => void;
  disabled?: boolean;
  maxSizeMB?: number;
  previewUrl?: string;
  className?: string;
}

const ACCEPTED_IMAGE_FORMATS = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];

export const ImageUploader = memo(function ImageUploader({
  onImageSelect,
  onRemove,
  disabled = false,
  maxSizeMB = FILE_SIZE_LIMITS.IMAGE_MAX_MB,
  previewUrl: externalPreviewUrl,
  className = '',
}: ImageUploaderProps) {
  const { t } = useTranslation();
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const displayPreview = externalPreviewUrl || previewUrl;

  const validateFile = (file: File): string | null => {
    // Check file type
    if (!ACCEPTED_IMAGE_FORMATS.includes(file.type)) {
      return t('image.imageUploader.invalidFormat', {
        defaultValue: 'Invalid file format. Please upload PNG, JPG, JPEG, WEBP, or GIF.',
      });
    }

    // Check file size
    const maxSizeBytes = maxSizeMB * MB;
    if (file.size > maxSizeBytes) {
      return t('image.imageUploader.fileTooLarge', {
        defaultValue: 'File size exceeds {{maxSize}}MB limit.',
        maxSize: maxSizeMB,
      });
    }

    return null;
  };

  const handleFile = useCallback(
    async (file: File) => {
      setError(null);
      setIsLoading(true);

      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        setIsLoading(false);
        return;
      }

      try {
        const base64 = await fileToBase64(file);
        setSelectedFile(file);
        onImageSelect(base64, file);

        // Create preview URL
        const url = URL.createObjectURL(file);
        setPreviewUrl(url);
      } catch {
        setError(
          t('image.imageUploader.readError', {
            defaultValue: 'Failed to read image file',
          })
        );
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [maxSizeMB, onImageSelect]
  );

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled) {
        setIsDragging(true);
      }
    },
    [disabled]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      if (disabled) return;

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        handleFile(files[0]);
      }
    },
    [disabled, handleFile]
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        handleFile(e.target.files[0]);
      }
    },
    [handleFile]
  );

  const handleClick = useCallback(() => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, [disabled]);

  const handleRemove = useCallback(() => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onRemove?.();
  }, [onRemove]);

  return (
    <div className={`w-full ${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_IMAGE_FORMATS.join(',')}
        onChange={handleFileInput}
        className="hidden"
        disabled={disabled}
      />

      {!displayPreview ? (
        <div
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={handleClick}
          className={`
            relative border-2 border-dashed rounded-lg p-8
            transition-all duration-200 cursor-pointer
            ${isDragging ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-300 dark:border-secondary-600 bg-gray-50 dark:bg-secondary-800/50'}
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20'}
          `}
        >
          <div className="flex flex-col items-center justify-center space-y-4">
            <div
              className={`
              w-16 h-16 rounded-full flex items-center justify-center
              ${isDragging ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-gray-100 dark:bg-secondary-700'}
            `}
            >
              <Upload
                className={`w-8 h-8 ${isDragging ? 'text-blue-600' : 'text-gray-400 dark:text-secondary-400'}`}
              />
            </div>

            <div className="text-center">
              <p className="text-lg font-medium text-gray-700 dark:text-secondary-200">
                {t('image.imageUploader.dragAndDrop', {
                  defaultValue: 'Drag and drop your image here',
                })}
              </p>
              <p className="text-sm text-gray-500 dark:text-secondary-400 mt-1">
                {t('image.imageUploader.orClickToSelect', {
                  defaultValue: 'or click to select a file',
                })}
              </p>
            </div>

            <div className="text-xs text-gray-400 dark:text-secondary-500 space-y-1 text-center">
              <p>
                {t('image.imageUploader.supportedFormats', {
                  defaultValue: 'Supported formats: PNG, JPG, JPEG, WEBP, GIF',
                })}
              </p>
              <p>
                {t('image.imageUploader.maxFileSize', {
                  defaultValue: 'Maximum file size: {{maxSize}}MB',
                  maxSize: maxSizeMB,
                })}
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="border border-gray-300 dark:border-secondary-600 rounded-lg p-6 bg-white dark:bg-secondary-800">
          <div className="flex items-start space-x-4">
            {/* Image preview */}
            <div className="flex-shrink-0">
              {displayPreview ? (
                <img
                  src={displayPreview}
                  alt="Preview"
                  className="w-32 h-32 object-cover rounded-lg"
                />
              ) : (
                <div className="w-32 h-32 bg-gray-100 dark:bg-secondary-700 rounded-lg flex items-center justify-center">
                  <ImageIcon className="w-12 h-12 text-gray-400 dark:text-secondary-500" />
                </div>
              )}
            </div>

            {/* File info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0 pr-4">
                  {selectedFile ? (
                    <>
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {selectedFile.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-secondary-400 mt-1">
                        {formatFileSize(selectedFile.size)} •{' '}
                        {selectedFile.type.split('/')[1].toUpperCase()}
                      </p>
                    </>
                  ) : (
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {t('image.imageUploader.imageReady', {
                        defaultValue: 'Image ready',
                      })}
                    </p>
                  )}
                </div>

                {!isLoading && (
                  <button
                    onClick={handleRemove}
                    className="flex-shrink-0 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-secondary-700 transition-colors"
                    aria-label="Remove file"
                  >
                    <X className="w-5 h-5 text-gray-400 dark:text-secondary-400" />
                  </button>
                )}
              </div>

              {/* Loading state */}
              {isLoading && (
                <p className="text-xs text-blue-600 mt-2">
                  {t('image.imageUploader.loading', {
                    defaultValue: 'Loading image...',
                  })}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}
    </div>
  );
});
